<script lang="ts" setup>
import { inject, ref } from "vue";
import { TranslationService } from "../../services/translation.service";
import { ToolsService } from "../../services/tools.service";
import { Atom, ATOMS } from "../../tools/atoms";
import { LANG } from "@libTs/cms.globals";
import { vInputText as vInputTextDir } from "../../directives/v-input-text";

const vInputText = vInputTextDir;

interface IPeriodicModel {
    element: string;
    category: any;
    atom: Atom | undefined;
    hasError: boolean;
}

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;
const toolsSvc = inject<ToolsService>("toolsSvc") as ToolsService;

const model = ref<IPeriodicModel>({
    element: "",
    category: undefined,
    atom: ATOMS[0],
    hasError: false,
});

const onKeyupElement = (): void => {
    model.value.atom = toolsSvc.findAtom(model.value.element, LANG);
};
</script>

<template>
    <div tool-periodictable class="tool-content-inner pt-4">
        <div class="periodic-table-grid">
            <div class="periodic-cell-el">
                <label class="text-f7">
                    {{ t("Element") }}
                </label>

                <div class="input-text rounded-s mt-2">
                    <input
                        class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s h-10"
                        type="text"
                        autocomplete="off"
                        :placeholder="`${t('Symbol')} / ${t('Name')}`"
                        v-model="model.element"
                        @keyup="onKeyupElement()"
                    />
                </div>
            </div>

            <label
                class="periodic-cell-el-detail-title text-small bold mt-10 mb-3"
            >
                {{ t("Element specification") }}
            </label>

            <div class="periodic-cell-atom">
                <div class="atom">
                    <span class="atom-tl bold" :title="t('Atomic number')">{{
                        model?.atom?.number
                    }}</span>
                    <span class="atom-tr" :title="t('Conv. atomic weight')">{{
                        model?.atom?.conventionalWeight
                    }}</span>
                    <span
                        class="atom-bl"
                        v-if="model?.atom?.firstIonization"
                        :title="`${t('First ionization energy')} (eV)`"
                        >{{ model.atom.firstIonization }}</span
                    >
                    <span
                        class="atom-br"
                        v-if="model?.atom?.electronegativity"
                        :title="`${t('Electro­negativity')} (Pauling)`"
                        >{{ model.atom.electronegativity }}</span
                    >
                    <div class="atom-m">
                        <div
                            class="atom-abbr"
                            :class="{
                                'color-dark-gray': model?.atom?.noStableIsotope,
                            }"
                        >
                            {{ model?.atom?.letter }}
                        </div>
                        <div class="atom-name">
                            {{ model?.atom?.title[LANG] }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="periodic-cell-atom-detail bgcolor-white">
                <table class="atom-detail">
                    <tbody>
                        <tr>
                            <td class="atom-detail-label">{{ t("Name") }}</td>
                            <td class="bold">
                                {{
                                    model?.atom?.letter +
                                    " / " +
                                    model?.atom?.title[LANG]
                                }}
                            </td>
                        </tr>
                        <tr>
                            <td class="atom-detail-label">
                                {{ t("Atomic number") }}
                            </td>
                            <td class="bold">{{ model?.atom?.number }}</td>
                        </tr>
                        <tr v-if="model?.atom?.conventionalWeight">
                            <td class="atom-detail-label">
                                {{ t("Conv. atomic weight") }}
                            </td>
                            <td class="bold">
                                {{ model?.atom?.conventionalWeight }}
                            </td>
                        </tr>
                        <tr v-if="model?.atom?.weight">
                            <td class="atom-detail-label">
                                {{ t("Std. atomic weight") }}
                            </td>
                            <td class="bold">
                                {{ model?.atom?.weight }}
                            </td>
                        </tr>
                        <tr v-if="model?.atom?.firstIonization">
                            <td class="atom-detail-label">
                                {{ t("First ionization energy") }}
                            </td>
                            <td class="bold">
                                {{ model?.atom?.firstIonization }} eV
                            </td>
                        </tr>
                        <tr v-if="model?.atom?.electronegativity">
                            <td class="atom-detail-label">
                                {{ t("Electro­negativity") }} (Pauling)
                            </td>
                            <td class="bold">
                                {{ model?.atom?.electronegativity }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
